/*eslint-disable*/

import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, Button, CircularProgress, TextField, IconButton, InputAdornment, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Dialog from '@mui/material/Dialog';
import toast from 'react-hot-toast';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';

import { useQueryClient } from 'react-query';
import { UserDetailedInformationDialog } from '../components/dialog';
// import { CheckinCheckout } from '../components/checkin';
import {
  FinalSessionSurveys,
  WelcomeSessionSurvey,
  SessionSurveyA,
  SessionSurveyB,
  SessionSurveyC,
  FirstSessionLog,
} from 'src/components/surveys';
import { getUserDetails } from '../utils';
import { useQueryGetSurveyResults } from '../hooks/react-query';
import CustomPagination from '../components/pagination/CustomPagination';

import { TableView } from '../components/table';

const SurveyResponses = () => {
  const userDetails = getUserDetails();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [currentResponse, setCurrentResponse] = useState({});
  const userRole = userDetails.role.toLowerCase();
  const userToken = userDetails?.token;

  const navigate = useNavigate();

  // State declarations
  const [open, setOpen] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0); // MUI pagination uses 0-based index
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [trainingEndingPayload, setTrainingEndingPayload] = useState({});
  const [displayEdifierDetails, setDisplayEdifierDetails] = useState(false);
  const [currentEdifier, setCurrentEdifier] = useState({});

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchedValue);
      setPage(0); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchedValue]);

  // Fetch survey results using the new API
  const { data: surveyData, isLoading, error } = useQueryGetSurveyResults(
    userToken,
    navigate,
    toast,
    page + 1, // API uses 1-based page
    rowsPerPage,
    debouncedSearch,
    '' // schoolId - can be added later if needed
  );

  const surveys = surveyData?.surveys || [];
  const pagination = surveyData?.pagination || {};

  // Apply client-side date filters
  const filteredSurveys = surveys.filter((item) => {
    if (!item.createdAt) return true;

    // Apply start date filter
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const itemDate = new Date(item.createdAt);
      itemDate.setHours(0, 0, 0, 0);
      if (itemDate < start) return false;
    }

    // Apply end date filter
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      const itemDate = new Date(item.createdAt);
      itemDate.setHours(23, 59, 59, 999);
      if (itemDate > end) return false;
    }

    return true;
  });


  const getSurveyByTitle = (currentResponse) => {
    const title = currentResponse?.title?.toLowerCase() || '';
    if (title === 'final session surveys') {
      return <FinalSessionSurveys prePopulatedData={currentResponse} />;
    } else if (title === 'welcome session survey') {
      return <WelcomeSessionSurvey prePopulatedData={currentResponse} />;
    } else if (title === 'session survey a') {
      return <SessionSurveyA prePopulatedData={currentResponse} />;
    } else if (title === 'session survey b') {
      return <SessionSurveyB prePopulatedData={currentResponse} />;
    } else if (title === 'session survey c') {
      return <SessionSurveyC prePopulatedData={currentResponse} />;
    } else if (title === '1st session log') {
      return <FirstSessionLog prePopulatedData={currentResponse} firstLog={true} />;
    } else if (title === 'all sessions log') {
      return <FirstSessionLog prePopulatedData={currentResponse} firstLog={false} />;
    } else {
      // Unknown survey type — render a generic read-only view so the dialog stays open
      return (
        <Box sx={{ p: 3, overflowY: 'auto', height: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            {currentResponse?.title || 'Survey Response'}
          </Typography>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>
            {JSON.stringify(currentResponse, null, 2)}
          </pre>
        </Box>
      );
    }
  };

  useEffect(() => {
    if (Object.keys(currentResponse).length > 0) {
      setOpen(true);
    }
  }, [currentResponse]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Recorded Responses</title>
      </Helmet>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          // scroll={scroll}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          sx={{
            '& .MuiPaper-root': { minWidth: '70%', height: '80%' },
          }}
        >
          {getSurveyByTitle(currentResponse)}
        </Dialog>
      )}

      <Container maxWidth="xl" sx={{ padding: '0 !important' }}>
        {/* Displaying users tags in a modal */}
        <UserDetailedInformationDialog
          open={displayEdifierDetails}
          handleClose={() => setDisplayEdifierDetails(false)}
          userData={currentEdifier}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              background: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { background: alpha(theme.palette.primary.main, 0.2) },
            }}
          >
            <ArrowBackIcon sx={{ color: 'primary.main' }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'var(--gradient-primary, linear-gradient(135deg, #A8469B 0%, #569cd3 100%))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Recorded Responses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse all submitted survey responses
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            mt: 3,
            alignItems: 'center',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          {/* Left side - Search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flex: { xs: '1 1 100%', md: '0 1 auto' },
              minWidth: { xs: '100%', md: '400px' },
            }}
          >
            <TextField
              placeholder="Search by Name or Survey Title"
              size="small"
              value={searchedValue}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', md: '400px' },
                '& .MuiInputBase-root': {
                  height: '40px',
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  py: 0,
                },
              }}
              onChange={(e) => setSearchedValue(e.target.value)}
            />
          </Box>

          {/* Right side - Date filters and Clear button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              flex: { xs: '1 1 100%', md: '0 1 auto' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                minWidth: { xs: '100%', md: '160px' },
                maxWidth: { xs: '100%', md: '160px' },
                '& .MuiInputBase-root': {
                  height: '40px',
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  py: 0,
                },
              }}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                minWidth: { xs: '100%', md: '160px' },
                maxWidth: { xs: '100%', md: '160px' },
                '& .MuiInputBase-root': {
                  height: '40px',
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  py: 0,
                },
              }}
              onChange={(e) => setEndDate(e.target.value)}
            />

            {(startDate || endDate || searchedValue) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                sx={{
                  height: '40px',
                  minHeight: '40px',
                  maxHeight: '40px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { xs: '100%', md: '120px' },
                }}
                onClick={() => {
                  setSearchedValue('');
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Box>

        {/* {isLoadingPairs && (
          <Box sx={{ textAlign: 'center', mt: '30px' }}>
            <CircularProgress />
          </Box>
        )} */}

        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4, mt: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4, mt: 3 }}>
            <Typography variant="body1" sx={{ color: 'error.main' }}>
              Error loading survey responses. Please try again.
            </Typography>
          </Box>
        ) : filteredSurveys.length > 0 ? (
          <>
            <TableView
              tableData={filteredSurveys}
              setOpen={setOpen}
              userRole={userDetails?.role.toLowerCase()}
              searchedValue={searchedValue}
              setTrainingEndingPayload={setTrainingEndingPayload}
              setCurrentResponse={(row) => {
                // When clicking View Response, use surveyData directly from the row
                if (row && row.surveyData && typeof row.surveyData === 'object') {
                  setCurrentResponse(row.surveyData);
                }
              }}
              responses
            />
            <Box sx={{ mt: 3 }}>
              <CustomPagination
                count={pagination.totalCount || 0}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(event.target.value);
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Responses per page:"
              />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, mt: 3 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {searchedValue || startDate || endDate
                ? 'No survey responses match your filters'
                : 'No survey responses found'}
            </Typography>
            {(startDate || endDate || searchedValue) && (
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => {
                  setSearchedValue('');
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SurveyResponses;
